import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Calendar,
  FileText,
  ClipboardList,
  CheckCircle2,
  Circle,
  MessageCircle,
} from "lucide-react";
import { useContent } from "@/content/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusMeta = {
  anfrage: { label: "Anfrage", cls: "border-foreground/25 text-muted-foreground" },
  in_arbeit: { label: "In Arbeit", cls: "border-primary text-primary" },
  pausiert: { label: "Pausiert", cls: "border-foreground/25 text-muted-foreground" },
  fertig: { label: "Fertig", cls: "border-green-500/60 text-green-400" },
};

const kindMeta = {
  bericht: { label: "Bericht", Icon: ClipboardList },
  termin: { label: "Termin", Icon: Calendar },
  dokument: { label: "Dokument", Icon: FileText },
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("de-DE");
};

export default function PublicProject() {
  const { token } = useParams();
  const content = useContent();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API}/portal/public/${token}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then((d) => setProject(d.project))
      .catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="max-w-sm text-center" data-testid="public-project-error">
          <img src="/rundum-logo.png" alt="Rundum Logo" className="mx-auto mb-8 h-10 w-auto object-contain" />
          <p className="font-heading text-2xl font-black text-foreground">Link ungültig oder abgelaufen.</p>
          <p className="mt-3 text-sm text-muted-foreground">
            Frag mich einfach kurz, dann schicke ich dir einen neuen Link.
          </p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Lade …</p>
      </div>
    );
  }

  const steps = project.steps || [];
  const updates = project.updates || [];
  const done = steps.filter((s) => s.done).length;
  const pct = steps.length ? Math.round((done / steps.length) * 100) : 0;
  const st = statusMeta[project.status] || statusMeta.in_arbeit;
  const johnWhatsApp = `https://wa.me/${content.contact.whatsapp}?text=${encodeURIComponent(
    `Hallo John, kurze Frage zu meinem Projekt „${project.title}": `
  )}`;

  return (
    <div className="min-h-screen bg-background" data-testid="public-project">
      <header className="border-b border-foreground/10">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <img src="/rundum-logo.png" alt="Rundum Logo" className="h-8 w-auto object-contain object-left" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Projekt-Update</span>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 py-12">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
          {project.customer_name ? `Hallo ${project.customer_name} – h` : "H"}ier ist der aktuelle Stand
        </p>
        <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
          <h1 className="font-heading text-3xl font-black tracking-tight text-foreground md:text-4xl" data-testid="public-project-title">
            {project.title}
          </h1>
          <span className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${st.cls}`} data-testid="public-project-status">
            {st.label}
          </span>
        </div>
        {project.address && (
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{project.address}</p>
        )}
        {project.description && (
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground md:text-base">{project.description}</p>
        )}

        {steps.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <span>{done} von {steps.length} Schritten erledigt</span>
              <span className="text-primary">{pct} %</span>
            </div>
            <div className="mt-2 h-1.5 w-full bg-foreground/10">
              <div className="h-full bg-primary" style={{ width: `${pct}%` }} data-testid="public-project-progress" />
            </div>
          </div>
        )}

        {project.next_appointment && (
          <div className="mt-6 flex items-center gap-3 border border-primary/40 bg-primary/10 px-4 py-3" data-testid="public-project-next">
            <Calendar className="h-4 w-4 shrink-0 text-primary" />
            <p className="text-sm text-foreground">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">Nächster Termin · </span>
              {project.next_appointment}
            </p>
          </div>
        )}

        {steps.length > 0 && (
          <div className="mt-10">
            <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.25em] text-primary">Zeitleiste</p>
            <ul>
              {steps.map((s, i) => (
                <li key={i} className="relative flex gap-4 pb-6 last:pb-0">
                  {i < steps.length - 1 && <span className="absolute left-[11px] top-6 h-full w-px bg-foreground/10" />}
                  {s.done ? (
                    <CheckCircle2 className="h-[22px] w-[22px] shrink-0 text-primary" />
                  ) : (
                    <Circle className="h-[22px] w-[22px] shrink-0 text-muted-foreground/50" />
                  )}
                  <div>
                    <p className={`text-sm ${s.done ? "text-foreground" : "text-muted-foreground"}`}>{s.title}</p>
                    {!s.done && i === steps.findIndex((x) => !x.done) && (
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-primary">Kommt als Nächstes</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {updates.length > 0 && (
          <div className="mt-10">
            <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.25em] text-primary">Berichte & Fotos</p>
            <ul className="space-y-4">
              {updates.map((u, i) => {
                const meta = kindMeta[u.kind] || kindMeta.bericht;
                const photos = u.photos || (u.photo ? [u.photo] : []);
                return (
                  <li key={i} className="border border-foreground/10 p-5">
                    <div className="flex gap-3">
                      <meta.Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {meta.label}{u.date ? ` · ${formatDate(u.date)}` : ""}
                        </p>
                        <p className="mt-1 text-sm text-foreground">{u.text}</p>
                        {u.link && (
                          <a href={u.link} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-xs text-primary underline underline-offset-2">
                            Öffnen
                          </a>
                        )}
                      </div>
                    </div>
                    {photos.length > 0 && (
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        {photos.map((ph, pi) => (
                          <a key={pi} href={`${API}/files/${ph}?magic=${token}`} target="_blank" rel="noopener noreferrer">
                            <img
                              src={`${API}/files/${ph}?magic=${token}`}
                              alt={`Baustellen-Foto ${pi + 1}`}
                              className="h-36 w-full border border-foreground/10 object-cover transition-opacity duration-300 hover:opacity-80"
                              data-testid={`public-photo-${i}-${pi}`}
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="mt-12 border border-foreground/10 bg-card p-6 text-center md:p-8">
          <p className="font-heading text-xl font-bold text-foreground">Fragen zum Projekt?</p>
          <p className="mt-2 text-sm text-muted-foreground">Schreib mir direkt – ich antworte so schnell ich kann.</p>
          <a
            href={johnWhatsApp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="public-project-whatsapp"
            className="mt-5 inline-flex items-center gap-3 rounded-full bg-primary px-7 py-4 font-heading text-sm font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp an John
          </a>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            Rundum · Ehrlich. Direkt. Verlässlich. · {content.contact.phone}
          </p>
        </div>
      </div>
    </div>
  );
}
